/**
 * The origin a link can be sent to somebody else.
 *
 * WHY THIS IS NOT window.location.origin
 *
 *     Inside the installed app that value is a capacitor:// or file:// URL.
 *     Not a different domain - not a URL the recipient's phone can open at
 *     all. Share a list from the installed app and the share sheet opens
 *     normally, the link copies, and the person receiving it gets something
 *     that goes nowhere. Nothing at either end indicates a fault.
 *
 * WHERE THIS CAME FROM (13 Sep 2026)
 *
 *     The reasoning already existed here, written once for the quick-login QR
 *     in utils/quickLoginQr.js - including the note about capacitor and file
 *     URLs. Nothing carried it to the list-share links, which built their URL
 *     from the raw origin in three separate places.
 *
 *     The Tradie's Bay chat found the identical split in their repo: the same
 *     resolver written once for their QR, and six other link builders using
 *     the raw origin. Third time this week that a correct piece of reasoning
 *     existed on one path and never reached its siblings.
 *
 * THE ORDER, and each step is deliberate:
 *
 *     1. A configured public URL wins. That is the deployment saying where it
 *        really lives.
 *     2. Otherwise the current origin IF it is genuinely http or https - so a
 *        link shared from preview points at preview, which is what you want
 *        while testing.
 *     3. Otherwise the production site, because a link to the wrong real
 *        place still opens; a capacitor:// link opens nothing.
 */

export const FALLBACK_ORIGIN = "https://xelanti.com";

/**
 * @param {{configured?: string, origin?: string}} opts
 * @returns {string} an origin with no trailing slash, always web-openable
 */
export function publicOrigin({ configured, origin } = {}) {
  let base = String(configured || "").trim();

  if (!base) {
    const o = String(origin || "").trim();
    // capacitor://, file://, and anything else a recipient cannot open are
    // rejected here rather than shipped inside a link.
    base = /^https?:\/\//i.test(o) ? o : "";
  }

  if (!base) base = FALLBACK_ORIGIN;
  return base.replace(/\/+$/, "");
}

/** The configured value, read in one place so call sites stay short. */
export function configuredPublicUrl() {
  return process.env.REACT_APP_PUBLIC_URL;
}

/** What every share-link builder should pass as its origin. */
export function shareOrigin() {
  return publicOrigin({
    configured: configuredPublicUrl(),
    origin: typeof window !== "undefined" ? window.location.origin : "",
  });
}
